import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { POLLING_INTERVAL_MS, VERSION_JSON_PATH } from '../constants';
import { isUpdateRequiredState } from '../state';

const currentVersion = __APP_VERSION__;

const fetchLatestVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch(`${VERSION_JSON_PATH}?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
};

export const useForceUpdate = () => {
  const setIsUpdateRequired = useSetAtom(isUpdateRequiredState);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkVersion = useCallback(async () => {
    const latestVersion = await fetchLatestVersion();
    if (latestVersion !== null && latestVersion !== currentVersion) {
      setIsUpdateRequired(true);
    }
  }, [setIsUpdateRequired]);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    checkVersion();

    intervalRef.current = setInterval(checkVersion, POLLING_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);
};
